/**
 * Mulaya careers application form → Google Apps Script web app.
 * POST JSON (text/plain to avoid CORS preflight) with CV as base64.
 */
(() => {
  const FALLBACK_CONFIG = {
    provinces: [
      'Albacete',
      'Asturias',
      'Álava',
      'Barcelona',
      'Burgos',
      'Cantabria',
      'Córdoba',
      'Guipúzcoa',
      'La Rioja',
      'León',
      'Madrid',
      'Murcia',
      'Navarra',
      'Pontevedra',
      'Salamanca',
      'Sevilla',
      'Tarragona',
      'Valencia',
      'Valladolid',
      'Vizcaya',
      'Zaragoza',
    ],
    citiesByProvince: {
      Albacete: ['Albacete'],
      Asturias: ['Oviedo'],
      Álava: ['Vitoria'],
      Barcelona: ['Barcelona', 'Granollers'],
      Burgos: ['Burgos'],
      Cantabria: ['Santander'],
      Córdoba: ['Córdoba'],
      Guipúzcoa: ['San Sebastián'],
      'La Rioja': ['Logroño'],
      León: ['León'],
      Madrid: ['Madrid'],
      Murcia: ['Murcia'],
      Navarra: ['Pamplona'],
      Pontevedra: ['Vigo'],
      Salamanca: ['Salamanca'],
      Sevilla: ['Sevilla'],
      Tarragona: ['Tarragona'],
      Valencia: ['Valencia'],
      Valladolid: ['Valladolid'],
      Vizcaya: ['Bilbao'],
      Zaragoza: ['Zaragoza'],
    },
    positions: [
      'Dependiente/a',
      'Encargado/a',
      'Area Manager',
      'Mozo de almacén',
      'Administrativo logística',
      'Administrativo oficinas centrales',
      'E-commerce',
    ],
  };

  class MulayaCareersForm extends HTMLElement {
    /** @type {{ provinces: string[], citiesByProvince: Record<string, string[]>, positions: string[] }} */
    #config = structuredClone(FALLBACK_CONFIG);

    connectedCallback() {
      if (this.dataset.initialized === '1') return;
      this.dataset.initialized = '1';

      this.#form = this.querySelector('[data-careers-form]');
      this.#submitButton = this.querySelector('[data-careers-submit]');
      this.#loadingText = this.querySelector('[data-careers-loading]');
      this.#progressShell = this.querySelector('[data-careers-progress]');
      this.#toastBackdrop = this.querySelector('[data-careers-toast]');
      this.#toastTitle = this.querySelector('[data-careers-toast-title]');
      this.#toastText = this.querySelector('[data-careers-toast-text]');
      this.#toastButton = this.querySelector('[data-careers-toast-close]');

      if (!this.#form || !this.#submitButton) return;

      this.#fields = {
        name: this.#form.querySelector('[name="name"]'),
        email: this.#form.querySelector('[name="email"]'),
        phone: this.#form.querySelector('[name="phone"]'),
        province: this.#form.querySelector('[name="province"]'),
        city: this.#form.querySelector('[name="city"]'),
        position: this.#form.querySelector('[name="position"]'),
        desiredWorkday: this.#form.querySelector('[name="desiredWorkday"]'),
        experienceYears: this.#form.querySelector('[name="experienceYears"]'),
        profile: this.#form.querySelector('[name="profile"]'),
        cv: this.#form.querySelector('[name="cv"]'),
        comments: this.#form.querySelector('[name="comments"]'),
        privacyConsent: this.#form.querySelector('[name="privacyConsent"]'),
      };

      this.#toastButton?.addEventListener('click', () => this.#closeToast());
      this.#toastBackdrop?.addEventListener('click', (event) => {
        if (event.target === this.#toastBackdrop) this.#closeToast();
      });
      this.#fields.province?.addEventListener('change', () => this.#handleProvinceChange());
      this.#form.addEventListener('submit', (event) => this.#onSubmit(event));

      this.#applyConfig(this.#config);
      this.#loadRemoteConfig();
    }

    get #endpoint() {
      return (this.dataset.endpoint || '').trim().replace(/\/$/, '');
    }

    get #maxCvMb() {
      const value = Number(this.dataset.maxCvMb || 7);
      return Number.isFinite(value) && value > 0 ? value : 7;
    }

    async #loadRemoteConfig() {
      if (!this.#endpoint) return;

      try {
        const url = `${this.#endpoint}?action=config`;
        const response = await fetch(url, {
          method: 'GET',
          redirect: 'follow',
          credentials: 'omit',
        });
        const data = await response.json();

        if (!data?.ok) {
          throw new Error(data?.message || 'No se pudo cargar la configuración.');
        }

        this.#applyConfig({
          provinces: Array.isArray(data.provinces) ? data.provinces : FALLBACK_CONFIG.provinces,
          citiesByProvince:
            data.citiesByProvince && typeof data.citiesByProvince === 'object'
              ? data.citiesByProvince
              : FALLBACK_CONFIG.citiesByProvince,
          positions: Array.isArray(data.positions) ? data.positions : FALLBACK_CONFIG.positions,
        });
      } catch {
        // Keep embedded fallback lists so the form remains usable.
      }
    }

    /** @param {{ provinces: string[], citiesByProvince: Record<string, string[]>, positions: string[] }} config */
    #applyConfig(config) {
      this.#config = config;
      this.#populateSelect(this.#fields.province, config.provinces, 'Selecciona una provincia');
      this.#populateSelect(this.#fields.position, config.positions, 'Selecciona un puesto');
      this.#resetCitySelect('Selecciona antes una provincia');
    }

    /**
     * @param {HTMLSelectElement | null} selectElement
     * @param {string[]} options
     * @param {string} placeholder
     */
    #populateSelect(selectElement, options, placeholder) {
      if (!selectElement) return;

      selectElement.innerHTML = '';
      const firstOption = document.createElement('option');
      firstOption.value = '';
      firstOption.textContent = placeholder;
      selectElement.appendChild(firstOption);

      for (const optionValue of options) {
        const option = document.createElement('option');
        option.value = optionValue;
        option.textContent = optionValue;
        selectElement.appendChild(option);
      }
    }

    /** @param {string} message */
    #resetCitySelect(message) {
      const city = this.#fields.city;
      if (!city) return;

      city.innerHTML = '';
      const option = document.createElement('option');
      option.value = '';
      option.textContent = message;
      city.appendChild(option);
      city.disabled = true;
    }

    #handleProvinceChange() {
      const province = this.#fields.province?.value.trim() || '';
      const cities = this.#config.citiesByProvince[province] || [];
      const city = this.#fields.city;
      if (!city) return;

      city.innerHTML = '';
      const firstOption = document.createElement('option');
      firstOption.value = '';
      firstOption.textContent = cities.length ? 'Selecciona una ciudad' : 'No hay ciudades disponibles';
      city.appendChild(firstOption);
      city.disabled = !cities.length;

      for (const cityName of cities) {
        const option = document.createElement('option');
        option.value = cityName;
        option.textContent = cityName;
        city.appendChild(option);
      }
    }

    /** @param {string} name */
    #getCheckedValue(name) {
      const checked = this.#form?.querySelector(`input[name="${name}"]:checked`);
      return checked instanceof HTMLInputElement ? checked.value : '';
    }

    #getFormDataObject() {
      return {
        name: this.#fields.name?.value.trim() || '',
        email: this.#fields.email?.value.trim() || '',
        phone: this.#fields.phone?.value.trim() || '',
        province: this.#fields.province?.value.trim() || '',
        city: this.#fields.city?.value.trim() || '',
        position: this.#fields.position?.value.trim() || '',
        availabilityStart: this.#getCheckedValue('availabilityStart'),
        desiredWorkday: this.#fields.desiredWorkday?.value.trim() || '',
        availableSchedule: this.#getCheckedValue('availableSchedule'),
        weekendAvailability: this.#getCheckedValue('weekendAvailability'),
        fashionExperience: this.#getCheckedValue('fashionExperience'),
        mulayaMember: this.#getCheckedValue('mulayaMember'),
        experienceYears: this.#fields.experienceYears?.value.trim() || '',
        profile: this.#fields.profile?.value.trim() || '',
        comments: this.#fields.comments?.value.trim() || '',
      };
    }

    #validateForm() {
      this.#clearErrors();
      const payload = this.#getFormDataObject();
      const cvFile = this.#fields.cv?.files?.[0];
      let isValid = true;

      const required = {
        name: 'El nombre es obligatorio.',
        email: 'El email es obligatorio.',
        phone: 'El teléfono es obligatorio.',
        province: 'Selecciona una provincia.',
        city: 'Selecciona una ciudad.',
        position: 'Selecciona un puesto.',
        availabilityStart: 'Selecciona una opción.',
        desiredWorkday: 'Selecciona una opción.',
        availableSchedule: 'Selecciona una opción.',
        weekendAvailability: 'Selecciona una opción.',
        fashionExperience: 'Selecciona una opción.',
        mulayaMember: 'Selecciona una opción.',
        experienceYears: 'Selecciona una opción.',
      };

      for (const [key, message] of Object.entries(required)) {
        if (!payload[key]) {
          this.#showFieldError(key, message);
          isValid = false;
        }
      }

      if (payload.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) {
        this.#showFieldError('email', 'Introduce un email válido.');
        isValid = false;
      }

      if (payload.phone && payload.phone.replace(/[^\d+]/g, '').length < 9) {
        this.#showFieldError('phone', 'Introduce un teléfono válido.');
        isValid = false;
      }

      if (payload.profile && payload.profile.length > 500) {
        this.#showFieldError('profile', 'El perfil no puede superar los 500 caracteres.');
        isValid = false;
      }

      if (payload.comments && payload.comments.length > 2000) {
        this.#showFieldError('comments', 'Los comentarios no pueden superar los 2000 caracteres.');
        isValid = false;
      }

      if (!cvFile) {
        this.#showFieldError('cv', 'Debes adjuntar tu CV en PDF.');
        isValid = false;
      } else {
        const isPdf = cvFile.type === 'application/pdf' || /\.pdf$/i.test(cvFile.name);
        if (!isPdf) {
          this.#showFieldError('cv', 'El archivo debe ser un PDF.');
          isValid = false;
        }
        if (cvFile.size > this.#maxCvMb * 1024 * 1024) {
          this.#showFieldError('cv', `El PDF no puede superar los ${this.#maxCvMb} MB.`);
          isValid = false;
        }
      }

      if (!this.#fields.privacyConsent?.checked) {
        this.#showFieldError('privacyConsent', 'Debes aceptar la política de privacidad.');
        isValid = false;
      }

      const honeypot = this.#form?.querySelector('[name="website"]');
      if (honeypot instanceof HTMLInputElement && honeypot.value.trim()) {
        isValid = false;
      }

      return isValid;
    }

    #clearErrors() {
      this.querySelectorAll('[data-careers-error]').forEach((el) => {
        el.hidden = true;
        el.textContent = '';
      });
    }

    /**
     * @param {string} fieldName
     * @param {string} message
     */
    #showFieldError(fieldName, message) {
      const el = this.querySelector(`[data-careers-error="${fieldName}"]`);
      if (!el) return;
      el.textContent = message;
      el.hidden = false;
    }

    /** @param {boolean} isLoading */
    #setLoadingState(isLoading) {
      this.#submitButton.disabled = isLoading;
      this.#loadingText?.classList.toggle('is-visible', isLoading);
      this.#progressShell?.classList.toggle('is-active', isLoading);

      for (const field of Object.values(this.#fields)) {
        if (field) field.disabled = isLoading;
      }

      this.#form?.querySelectorAll('input[type="radio"]').forEach((radio) => {
        radio.disabled = isLoading;
      });

      if (!isLoading && this.#fields.city && !this.#fields.province?.value) {
        this.#fields.city.disabled = true;
      }
    }

    /**
     * @param {string} title
     * @param {string} text
     */
    #openToast(title, text) {
      if (!this.#toastBackdrop || !this.#toastTitle || !this.#toastText) return;
      this.#toastTitle.textContent = title;
      this.#toastText.textContent = text;
      this.#toastBackdrop.classList.add('is-visible');
      this.#toastBackdrop.setAttribute('aria-hidden', 'false');
      this.#toastButton?.focus();
    }

    #closeToast() {
      this.#toastBackdrop?.classList.remove('is-visible');
      this.#toastBackdrop?.setAttribute('aria-hidden', 'true');
      this.#submitButton?.focus();
    }

    /**
     * @param {File} file
     * @returns {Promise<{ base64: string, fileName: string, mimeType: string }>}
     */
    #readFileAsBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = String(reader.result || '');
          const commaIndex = result.indexOf(',');
          const base64 = commaIndex >= 0 ? result.slice(commaIndex + 1) : result;
          if (!base64) {
            reject(new Error('No se pudo leer el CV.'));
            return;
          }
          resolve({
            base64,
            fileName: file.name,
            mimeType: file.type || 'application/pdf',
          });
        };
        reader.onerror = () => reject(new Error('No se pudo leer el CV.'));
        reader.readAsDataURL(file);
      });
    }

    /** @param {SubmitEvent} event */
    async #onSubmit(event) {
      event.preventDefault();
      if (this.#submitButton.disabled) return;
      if (!this.#validateForm()) return;

      if (!this.#endpoint) {
        this.#openToast(
          'Formulario no configurado',
          'Falta la URL del Apps Script en la sección del tema (editor → Formulario trabaja con nosotros).'
        );
        return;
      }

      const cvFile = this.#fields.cv?.files?.[0];
      if (!cvFile) return;

      this.#setLoadingState(true);

      try {
        const cv = await this.#readFileAsBase64(cvFile);
        const payload = {
          ...this.#getFormDataObject(),
          cvBase64: cv.base64,
          cvFileName: cv.fileName,
          cvMimeType: cv.mimeType,
        };

        const response = await fetch(this.#endpoint, {
          method: 'POST',
          redirect: 'follow',
          credentials: 'omit',
          headers: {
            'Content-Type': 'text/plain;charset=utf-8',
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json().catch(() => null);
        if (!response.ok || !data?.ok) {
          throw new Error(data?.message || 'No se pudo enviar la candidatura.');
        }

        this.#form.reset();
        this.#resetCitySelect('Selecciona antes una provincia');
        this.#openToast(
          'Candidatura enviada',
          'Tu candidatura se ha enviado correctamente. En breve recibirás un email de confirmación.'
        );
      } catch (error) {
        const message =
          error instanceof Error && error.message
            ? error.message
            : 'Ha ocurrido un error al enviar la candidatura. Inténtalo de nuevo.';
        this.#openToast('No se ha podido enviar', message);
      } finally {
        this.#setLoadingState(false);
      }
    }

    /** @type {HTMLFormElement | null} */
    #form = null;
    /** @type {HTMLButtonElement | null} */
    #submitButton = null;
    /** @type {HTMLElement | null} */
    #loadingText = null;
    /** @type {HTMLElement | null} */
    #progressShell = null;
    /** @type {HTMLElement | null} */
    #toastBackdrop = null;
    /** @type {HTMLElement | null} */
    #toastTitle = null;
    /** @type {HTMLElement | null} */
    #toastText = null;
    /** @type {HTMLButtonElement | null} */
    #toastButton = null;
    /** @type {Record<string, HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null>} */
    #fields = {};
  }

  if (!customElements.get('mulaya-careers-form')) {
    customElements.define('mulaya-careers-form', MulayaCareersForm);
  }
})();
